import { allStatuses } from '@utils/helpers';
import { getLocationAsText, isRideshareContractor } from '@src/utils';
import { IMAGE_STORAGE_URL } from '@src/utils/constants';
import {
  DRIVER_CHAT_ACTIVE_STATUS_IDS,
  DRIVER_CHAT_ENDED_STATUS_ID,
  DRIVER_CHAT_GROUP_WINDOW_MS,
} from '@component/DriverChat/constants';
import {
  ChatMessage,
  ChatSender,
  DriverChatSeed,
} from '@component/DriverChat/types';

type StatusInfo = { value?: string; group?: string } | undefined;

const getStatusInfo = (statusId?: number | null): StatusInfo =>
  statusId == null
    ? undefined
    : (allStatuses as Record<number, StatusInfo>)[statusId];

/** Chat button window: Enroute to pickup through Departed from drop-off. */
export const isChatEnabledStatus = (statusId?: number | null) =>
  statusId != null && DRIVER_CHAT_ACTIVE_STATUS_IDS.includes(Number(statusId));

/** Live chat window: the conversation ends once the driver departs from drop-off. */
export const isChatOpenStatus = (statusId?: number | null) =>
  isChatEnabledStatus(statusId) &&
  Number(statusId) !== DRIVER_CHAT_ENDED_STATUS_ID;

/** Chat is only offered for segments assigned to Lyft or Uber. */
export const isChatEligibleContractor = (contractor: any) => {
  if (!contractor) return false;
  // Rows from the Assignment grid carry only the contractor name.
  if (typeof contractor === 'string') {
    return isRideshareContractor({
      id: 0,
      label: contractor,
      value: contractor,
    });
  }
  // A bare id (number) cannot be resolved to Lyft/Uber here.
  if (typeof contractor !== 'object') return false;
  return isRideshareContractor({
    ...contractor,
    label: contractor.label ?? contractor.name,
  });
};

export const getStatusLabel = (statusId?: number | null) =>
  getStatusInfo(statusId)?.value ?? '';

export const getInitials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || '?';

/** "Joan Delgado" -> "J. D•••••". Revealed only through an explicit Show action. */
export const maskMemberName = (first = '', last = '') => {
  if (!first && !last) return '';
  const lastInitial = last ? `${last[0].toUpperCase()}•••••` : '';
  return `${first ? `${first[0].toUpperCase()}.` : ''} ${lastInitial}`.trim();
};

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const shortTimeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});

const dayFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
});

/** "10:04 AM" — rendered in the viewer's local timezone, no zone suffix. */
export const formatChatTime = (iso: string) => {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : timeFormatter.format(date);
};

export const formatChatShortTime = (iso?: string) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? '' : shortTimeFormatter.format(date);
};

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const getDayLabel = (iso: string) => {
  const date = new Date(iso);
  const diffDays = Math.round(
    (startOfDay(new Date()) - startOfDay(date)) / 86_400_000
  );
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return dayFormatter.format(date);
};

const normalizeName = (name?: string | null) =>
  (name ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

export const formatColleagueName = (name?: string) => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0];
  const last = parts[parts.length - 1];
  // Already in "First L" / "First L." form — leave it as is.
  if (/^\p{L}\.?$/u.test(last)) return parts.join(' ');
  return `${parts[0]} ${last[0].toUpperCase()}`;
};

/** "{confirmation code}-{segment number}", falling back to what is known. */
export const getSegmentReference = (
  seed: Pick<DriverChatSeed, 'segmentId' | 'confirmationCode' | 'segmentNumber'>
) =>
  seed.confirmationCode
    ? `${seed.confirmationCode}-${seed.segmentNumber ?? seed.segmentId}`
    : `#${seed.segmentNumber ?? seed.segmentId}`;

/** Splits a "CODE-2" ride order id into its confirmation code and segment number. */
export const splitRideOrderId = (rideOrderId?: string | null) => {
  const value = (rideOrderId ?? '').trim();
  const at = value.lastIndexOf('-');
  return at > 0
    ? {
        confirmationCode: value.slice(0, at),
        segmentNumber: value.slice(at + 1),
      }
    : { confirmationCode: value || undefined, segmentNumber: undefined };
};

/** Member photos are stored as a path; the image host turns it into a URL. */
export const toMemberImageUrl = (image?: string | null) => {
  if (!image) return undefined;
  return /^(https?:|data:|blob:)/i.test(image)
    ? image
    : `${IMAGE_STORAGE_URL}/${image}`;
};

export const getMemberFullName = (
  seed: Pick<DriverChatSeed, 'memberFirstName' | 'memberLastName'>
) => [seed.memberFirstName, seed.memberLastName].filter(Boolean).join(' ');

export type ChatViewer = {
  firstName?: string;
  lastName?: string;
} | null;

/**
 * Whether a dispatcher message was written by the viewer. Every dispatcher of
 * the payor can write in the same conversation, so DISPATCHER alone is not "me".
 * Users are recognised by name: `senderName` is compared with the viewer's
 * first and last name. A message still being sent (optimistic) is always the
 * viewer's own.
 */
export const isOwnMessage = (message: ChatMessage, viewer: ChatViewer) => {
  if (message.sender !== 'DISPATCHER') return false;
  if (message.id === message.clientMessageId) return true;
  const mine = normalizeName(
    `${viewer?.firstName ?? ''} ${viewer?.lastName ?? ''}`
  );
  // Unknown sender name: treat as the viewer's own, as before.
  return (
    !message.senderName ||
    (!!mine && normalizeName(message.senderName) === mine)
  );
};

export type MessageListItem =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'new'; key: string }
  | { kind: 'system'; key: string; message: ChatMessage }
  | {
      kind: 'group';
      key: string;
      sender: Exclude<ChatSender, 'SYSTEM'>;
      /** The viewer's own messages: right side, no name. */
      isOwn: boolean;
      /** Who wrote it, for everyone except the viewer. */
      authorName?: string;
      messages: ChatMessage[];
    };

/**
 * Flattens messages into render items: day separators, a "New messages"
 * divider before `firstUnreadId`, system events, and sender groups.
 */
export const buildMessageListItems = (
  messages: ChatMessage[],
  firstUnreadId: string | null,
  viewer: ChatViewer = null
): MessageListItem[] => {
  const items: MessageListItem[] = [];
  let lastDay = '';
  let currentGroup: Extract<MessageListItem, { kind: 'group' }> | null = null;

  messages.forEach((message) => {
    const day = getDayLabel(message.createdAt);
    if (day !== lastDay) {
      items.push({ kind: 'day', key: `day-${message.id}`, label: day });
      lastDay = day;
      currentGroup = null;
    }
    if (message.id === firstUnreadId) {
      items.push({ kind: 'new', key: `new-${message.id}` });
      currentGroup = null;
    }
    if (message.sender === 'SYSTEM') {
      items.push({ kind: 'system', key: message.id, message });
      currentGroup = null;
      return;
    }
    const isOwn = isOwnMessage(message, viewer);
    const authorName = isOwn
      ? undefined
      : message.sender === 'DISPATCHER'
        ? formatColleagueName(message.senderName)
        : message.senderName;
    const previous = currentGroup?.messages[currentGroup.messages.length - 1];
    const fitsGroup =
      currentGroup &&
      currentGroup.sender === message.sender &&
      currentGroup.isOwn === isOwn &&
      currentGroup.authorName === authorName &&
      previous &&
      new Date(message.createdAt).getTime() -
        new Date(previous.createdAt).getTime() <
        DRIVER_CHAT_GROUP_WINDOW_MS;

    if (fitsGroup && currentGroup) {
      currentGroup.messages.push(message);
    } else {
      currentGroup = {
        kind: 'group',
        key: `group-${message.id}`,
        sender: message.sender,
        isOwn,
        authorName,
        messages: [message],
      };
      items.push(currentGroup);
    }
  });

  return items;
};

export const createClientMessageId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `cm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** Builds the chat seed from a monitoring or ride segment (driver/vehicle already parsed). */
export const buildSeedFromSegment = (segment: any): DriverChatSeed | null => {
  if (!segment?.id) return null;
  const { driver = {}, vehicle = {} } = segment;
  // The Assignment grid only has the member's full name as a string.
  const member =
    typeof segment.member === 'string'
      ? {
          firstName: segment.member.trim().split(/\s+/)[0],
          lastName: segment.member.trim().split(/\s+/).slice(1).join(' '),
        }
      : segment.member;
  const vehicleLabel = [vehicle?.color, vehicle?.make, vehicle?.model]
    .filter(Boolean)
    .join(' ');

  return {
    segmentId: Number(segment.id),
    rideId: segment.rideId != null ? Number(segment.rideId) : null,
    driverFirstName: driver?.first_name ?? '',
    driverImage: driver?.image_url,
    driverPhone: driver?.phone_number,
    vehicleLabel: vehicleLabel || undefined,
    licensePlate: vehicle?.license_plate,
    statusId: segment.statusId != null ? Number(segment.statusId) : null,
    pickupAddress: segment.pickupLocations
      ? getLocationAsText(segment.pickupLocations, true)
      : undefined,
    pickupTime: segment.scheduledPickupTime ?? undefined,
    dropOffAddress: segment.dropOffLocation
      ? getLocationAsText(segment.dropOffLocation, true)
      : undefined,
    dropOffTime: segment.scheduledAppointmentTime ?? undefined,
    memberFirstName: member?.firstName,
    memberLastName: member?.lastName,
    memberImage: toMemberImageUrl(
      member?.image ?? member?.image_url ?? member?.imageUrl
    ),
    confirmationCode:
      segment.confirmationCode ?? segment.rideOrderConfirmationCode,
    segmentNumber:
      segment.segmentNumber != null ? String(segment.segmentNumber) : undefined,
  };
};

/** Seed fields win when present: they come from the live segment on screen. */
export const mergeChatSeed = <T extends DriverChatSeed>(
  base: T | undefined,
  seed: DriverChatSeed
): T => {
  const merged = { ...(base ?? {}) } as T;
  (Object.keys(seed) as Array<keyof DriverChatSeed>).forEach((key) => {
    const value = seed[key];
    if (value !== undefined && value !== null && value !== '') {
      (merged as DriverChatSeed)[key] = value as never;
    }
  });
  return merged;
};
